import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    COLLABORATION_METHOD_OPTION_KEY,
    COLLABORATION_METHOD_SYNC,
    DESIGN_LIVE_REVIEW_METHOD,
} from '@99designs/design-services-common';
import {
    ExpertServiceDocument,
    ExpertServiceQuery,
    ExpertServiceQueryVariables,
} from '../../../../../graph/ExpertService.generated';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useServiceProductOptions } from '../../../hooks/useServiceProductOptions';
import { useUpdateField } from '../../useUpdateField';
import { useBriefSetProductDropdownMutation } from './products.generated';
import { useSelectProductWithCacheUpdate } from './useSelectProductMutation';

export function useUpdateProductDropdown(
    key: string,
    briefId?: string,
    initialValue?: string,
    groupId?: string
) {
    const client = useApolloClient();
    const [selectProduct] = useSelectProductWithCacheUpdate();
    const { serviceProductMpvId, serviceProductOptions } = useBriefContext();
    const fetchServiceProductOptions = useServiceProductOptions();

    const [setProductDropdown] = useBriefSetProductDropdownMutation();

    const updateField = useUpdateField({
        key,
        setField: setProductDropdown,
        briefId,
        initialValue,
    });

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);
            const briefData = result?.data;
            if (!briefData?.setField) {
                return;
            }

            const fields = briefData.setField.dynamicFields.filter((field) => {
                const matchesKey = field.id === key;
                if (!groupId) return matchesKey;
                const fieldGroupId = (field as unknown as { groupId?: string }).groupId;
                return matchesKey && fieldGroupId === groupId;
            });
            if (fields.length === 0) {
                return;
            }

            const correlationId = briefData.setField.correlationId;
            if (!correlationId) {
                return;
            }

            const collaborationMethod = serviceProductOptions?.find(
                (o) => o.key === COLLABORATION_METHOD_OPTION_KEY
            )?.value;

            const isDesignLive =
                serviceProductMpvId.toLocaleLowerCase() ===
                    DESIGN_LIVE_REVIEW_METHOD.toLocaleLowerCase() ||
                collaborationMethod === COLLABORATION_METHOD_SYNC;

            await Promise.all(
                fields.map(async (field) => {
                    if (field.__typename !== 'ClientConfiguredField') {
                        throw new Error(
                            "Field is not a ClientConfiguredField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                        );
                    }
                    const formValue = field.clientConfiguredValue;
                    if (!formValue) {
                        return;
                    }

                    // Fetch expert service using client.query (lifecycle-independent)
                    const { data: esData } = await client.query<
                        ExpertServiceQuery,
                        ExpertServiceQueryVariables
                    >({
                        query: ExpertServiceDocument,
                        variables: { mpvId: formValue },
                    });

                    // This is a stopgap to handle the case where designlive is selected
                    // but the product doesn't actually support the collaboration method (DesignLive as an option).
                    // or it's missing the otherDesign product collaboration method option.
                    // In that case, we want to set the product to the designLive product,
                    // not the actual mpvId of the product
                    // This can be removed once all products
                    // support the collaboration method.
                    const newServiceProduct = esData?.expertService?.serviceProduct;
                    const newServiceProductMpvId = newServiceProduct?.mpvId;

                    let supportsCollaborationMethod = false;
                    if (newServiceProduct?.productKey && newServiceProduct.pimMerchantVersion) {
                        const result = await fetchServiceProductOptions(
                            newServiceProduct.productKey,
                            newServiceProduct.pimMerchantVersion
                        );
                        supportsCollaborationMethod = result.supportsCollaborationMethod;
                    }

                    const designLiveMpvId =
                        isDesignLive && supportsCollaborationMethod
                            ? newServiceProductMpvId
                            : DESIGN_LIVE_REVIEW_METHOD;

                    const serviceProductMpvIdForSelect = isDesignLive
                        ? designLiveMpvId
                        : newServiceProductMpvId;

                    await selectProduct({
                        variables: {
                            requestId: correlationId,
                            input: {
                                printProductMpvId: [formValue],
                                serviceProductMpvId: serviceProductMpvIdForSelect,
                            },
                        },
                    });
                })
            );
        },
        [
            updateField,
            key,
            groupId,
            serviceProductMpvId,
            serviceProductOptions,
            fetchServiceProductOptions,
            selectProduct,
            client,
        ]
    );
}

export default useUpdateProductDropdown;
